import { useState } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { geneticsApi } from '../../api/genetics';

interface AddMutationAlleleFormProps {
  locusId: string;
  locusSymbol: string;
  onCreated: () => void;
  onCancel: () => void;
}

export default function AddMutationAlleleForm({
  locusId,
  locusSymbol,
  onCreated,
  onCancel,
}: AddMutationAlleleFormProps) {
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!symbol.trim() || !name.trim()) {
      setError('Symbol and name are required.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await geneticsApi.createAllele(locusId, {
        symbol: symbol.trim(),
        name: name.trim(),
        isWildType: false,
      });
      onCreated();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add allele');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 rounded border border-dashed border-slate-300 bg-slate-50 p-2 space-y-2">
      <p className="text-xs font-medium text-slate-700">Add allele to {locusSymbol}</p>
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={symbol}
          onChange={(e) => setSymbol(e.target.value)}
          placeholder="Symbol"
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Name"
          className="rounded border border-slate-300 px-2 py-1 text-xs"
        />
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={saving}
          className="inline-flex items-center gap-1 rounded bg-indigo-600 px-2 py-1 text-xs text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          <PlusIcon className="h-3.5 w-3.5" />
          {saving ? 'Saving…' : 'Add'}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-slate-600 hover:text-slate-800">
          Cancel
        </button>
      </div>
    </div>
  );
}
